import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { FieldErrors, FieldValues, UseFormTrigger } from 'react-hook-form';
import SharedFormStepperNew, {
  FormSubSteps,
  StepConfig,
} from './shared-form-stepper-new';
import ZodValidationDialog from './zod-validation-dialog';
import { Status } from './status-chip';
import SimpleFormHeaderNew from './simple-form-header-new';
import { getSchemaKeys } from './simple-form-page';
import SharedButton from './buttons/shared-button';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

interface SimpleFormPageNewProps<T extends FieldValues> {
  title: string;
  trigger: UseFormTrigger<T>;
  steps: StepConfig[];
  content: ReactNode;
  handleBack?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleNext?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleDiscard?: () => void;
  handleSubmit: () => void;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  isDisableSubmit?: boolean;
  isLoadingSubmit: boolean;
  submitButtonText?: string;
  stepCompleted: boolean[];
  setStepCompleted: Dispatch<SetStateAction<boolean[]>>;
  firstIdWithError?: string;
  setExpandedIds?: Dispatch<SetStateAction<Set<string>>>;
  errors: FieldErrors<T>;
  lastSavedDate?: number;
  /** The words before the date, e.g. "Last published"; defaults to "Last saved". */
  lastSavedLabel?: string;
  hasBeenSaved?: boolean;
  handleSaveAndClose?: () => void;
  isSaving?: boolean;
  isFormDirty?: boolean;
  status?: Status;
  handleStepClick?: (index: number) => void;
  subStep?: FormSubSteps[];
  /**
   * Optional page-level actions rendered in the header (e.g. Cancel / Save Draft / Publish). When
   * provided, the footer's Cancel button is hidden so it isn't shown twice.
   */
  headerActions?: ReactNode;
  /** Optional content beside the title, such as a status chip. */
  titleAdornment?: ReactNode;
  /** Optional notice between the header and the form card, such as a warning banner. */
  contentBanner?: ReactNode;
  /** Optional panel rendered to the right of the form card (e.g. a live preview). */
  sidePanel?: ReactNode;
  /** Hides the footer Continue/Finish button, for steps that advance from their own content. */
  hideNext?: boolean;
}

export default function SimpleFormPageNew<T extends FieldValues>({
  title,
  trigger,
  steps,
  content,
  handleBack,
  handleNext,
  handleDiscard,
  handleSubmit,
  activeStep,
  setActiveStep,
  isDisableSubmit,
  isLoadingSubmit,
  submitButtonText,
  stepCompleted,
  setStepCompleted,
  firstIdWithError,
  setExpandedIds,
  errors,
  lastSavedDate,
  lastSavedLabel,
  handleSaveAndClose,
  isSaving,
  isFormDirty,
  status,
  handleStepClick,
  subStep,
  headerActions,
  titleAdornment,
  sidePanel,
  contentBanner,
  hideNext = false,
}: SimpleFormPageNewProps<T>) {
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  const styles = {
    main: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
    },
    body: {
      display: 'flex',
      flexDirection: 'row',
      gap: 2,
      flex: 1,
      minHeight: 0,
    },
    sidePanel: {
      width: 350,
      flexShrink: 0,
      minHeight: 0,
      overflowY: 'auto',
    },
    contentColumnWithBanner: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      flex: 1,
      minWidth: 0,
      minHeight: 0,
    },
    card: {
      width: '100%',
      minWidth: 0,
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      p: 1,
      borderRadius: 2,
    },
    bodyCard: { flex: 1, minHeight: 0, overflow: 'hidden', width: '100%' },
    stepper: {
      p: 2,
      minHeight: 0,
      maxHeight: '100%',
      overflowY: 'auto',
      width: '17%',
      borderRadius: '4px',
    },
    contentColumn: {
      p: 2,
      pl: 1,
      pr: 2,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '83%',
      minHeight: 0,
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      minHeight: 0,
      p: 2,
      pt: 0,
      mt: 0.5,
    },
    footer: {
      p: 3,
      pt: 0,
      pb: 1.5,
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexShrink: 0,
    },
  };

  const handleDefaultNext = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const fields = steps[activeStep].schema
      ? getSchemaKeys(steps[activeStep].schema as any)
      : [];
    const isValid = await trigger(fields as any);

    if (isValid) {
      setActiveStep((prevState) => prevState + 1);
      setStepCompleted((prevState) => {
        const newCompleted = [...prevState];
        newCompleted[activeStep] = true;
        return newCompleted;
      });
    }
    if (firstIdWithError && setExpandedIds) {
      setExpandedIds(new Set([firstIdWithError]));

      setTimeout(() => {
        const element = document.getElementById(firstIdWithError);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };
  const handleDefaultBack = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const fields = steps[activeStep].schema
      ? getSchemaKeys(steps[activeStep].schema as any)
      : [];
    const isValid = await trigger(fields as any);
    setActiveStep((prevState) => prevState - 1);
    setStepCompleted((prevState) => {
      const newCompleted = [...prevState];
      newCompleted[activeStep] = isValid;
      return newCompleted;
    });
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (activeStep === steps.length - 1) {
      const isValid = await trigger();
      if (!isValid) {
        if (steps.length > 1) setShowWarningDialog(true);
      } else {
        handleSubmit();
      }
      return;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && handleDiscard) {
        event.preventDefault();
        handleDiscard();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleDiscard]);

  const formCard = (
    <Card sx={styles.card}>
      <Grid container direction="column" sx={{ height: '100%' }}>
        <Grid container sx={styles.bodyCard}>
          {/* Stepper */}
          {steps && (
            <Grid sx={styles.stepper}>
              <SharedFormStepperNew
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                steps={steps}
                errors={errors}
                trigger={trigger}
                stepCompleted={stepCompleted}
                setStepCompleted={setStepCompleted}
                handleStepClick={handleStepClick}
                subStep={subStep}
              />
            </Grid>
          )}
          {/* Content Area */}
          <Grid sx={styles.contentColumn}>
            <Box sx={styles.content}>{content}</Box>
          </Grid>
        </Grid>

        {/* Footer */}
        <Grid sx={styles.footer}>
          <Stack direction="row" spacing={2}>
            {!headerActions && (
              <SharedButton variant="secondary" onClick={handleDiscard}>
                Cancel
              </SharedButton>
            )}
            {handleSaveAndClose && (
              <SharedButton
                variant="primary"
                disabled={!isFormDirty}
                onClick={handleSaveAndClose}
              >
                Save and Close
              </SharedButton>
            )}
            {!(activeStep === 0) && (
              <SharedButton
                variant="secondary"
                onClick={handleBack ? handleBack : handleDefaultBack}
              >
                Back
              </SharedButton>
            )}
            {!hideNext && (
              <SharedButton
                variant="primary"
                loading={isLoadingSubmit}
                disabled={isDisableSubmit ?? false}
                type={activeStep === steps.length - 1 ? 'submit' : 'button'}
                onClick={
                  activeStep === steps.length - 1
                    ? undefined
                    : handleNext ?? handleDefaultNext
                }
              >
                {activeStep === steps.length - 1
                  ? submitButtonText ?? 'Finish'
                  : 'Continue'}
              </SharedButton>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );

  return (
    <>
      <Box
        component="form"
        onSubmit={handleOnSubmit}
        sx={styles.main}
        noValidate
      >
        {/* Title */}
        <SimpleFormHeaderNew
          title={title}
          lastSavedDate={lastSavedDate}
          lastSavedLabel={lastSavedLabel}
          isSaving={isSaving}
          status={status}
          actions={headerActions}
          titleAdornment={titleAdornment}
        />
        <Box sx={styles.body}>
          {contentBanner ? (
            // A notice above the form card, beside the side panel.
            <Box sx={styles.contentColumnWithBanner}>
              {contentBanner}
              {formCard}
            </Box>
          ) : (
            formCard
          )}
          {sidePanel && <Box sx={styles.sidePanel}>{sidePanel}</Box>}
        </Box>
      </Box>
      {showWarningDialog && (
        <ZodValidationDialog
          open={showWarningDialog}
          handleClose={() => setShowWarningDialog(false)}
          errors={errors}
        />
      )}
    </>
  );
}
